import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV = [
  { key: "submissions", label: "Submissions",        icon: "SB", to: "/admin",         match: (p) => p === "/admin" || p.startsWith("/admin/submissions") },
  { key: "inbox",       label: "Inbox",              icon: "IN", to: "/admin/inbox",   match: (p) => p.startsWith("/admin/inbox") },
  { key: "pricing",     label: "Pricing & rate card", icon: "PR", to: "/admin/pricing", match: (p) => p.startsWith("/admin/pricing") },
  { key: "content",     label: "Content",            icon: "CT", to: "/admin/content", match: (p) => p.startsWith("/admin/content") },
  { key: "team",        label: "Team",               icon: "TM", to: "/admin/team",    match: (p) => p.startsWith("/admin/team") },
];

const COLLAPSE_KEY = "folk.admin.sidebar.collapsed.v1";

function loadCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
}

/**
 * Shared admin layout. Left rail with primary navigation, right pane renders `children`.
 * The rail can be collapsed to a 68px icon-only strip; state is persisted to localStorage.
 * A live unread count is fetched from /admin/queries and shown as a badge on Inbox.
 */
export default function AdminShell({ title, eyebrow = "Admin", right, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [inboxOpen, setInboxOpen] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Poll open-message count so the Inbox badge feels live.
  useEffect(() => {
    let alive = true;
    const load = () => axios
      .get(`${API}/admin/queries`, { withCredentials: true })
      .then((res) => {
        if (!alive) return;
        const openCount = (res.data || []).filter((q) => q.status === "open").length;
        setInboxOpen(openCount);
      })
      .catch(() => { /* ignore */ });
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    navigate("/login");
  };

  const railWidth = collapsed ? 68 : 240;

  return (
    <div className="admin-shell" style={{ display: "grid", gridTemplateColumns: `${railWidth}px 1fr`, minHeight: "100vh", background: "var(--folk-bg)", transition: "grid-template-columns 0.2s ease" }}>
      <aside
        data-testid="admin-shell-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        style={{
          borderRight: "1.5px solid var(--folk-border)",
          background: "var(--folk-card)",
          padding: collapsed ? "20px 8px" : "28px 16px",
          position: "sticky", top: 0, height: "100vh",
          display: "flex", flexDirection: "column", gap: 6,
          overflow: "hidden",
        }}
      >
        <Link to="/admin" style={{ textDecoration: "none", color: "inherit", marginBottom: 18, alignSelf: collapsed ? "center" : "flex-start", overflow: "hidden" }}>
          {collapsed ? (
            <div style={{ width: 32, height: 32, borderRadius: 999, background: "#68280E", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display', serif", fontSize: 14, letterSpacing: "-0.02em" }}>
              fo
            </div>
          ) : (
            <Logo variant="split" size={20} />
          )}
        </Link>

        {!collapsed && (
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600, padding: "8px 12px 4px" }}>
            Workspace
          </div>
        )}

        {NAV.map((item) => {
          const active = item.match(location.pathname);
          const badge = item.key === "inbox" && inboxOpen > 0 ? inboxOpen : null;
          return (
            <Link
              key={item.key}
              to={item.to}
              data-testid={`admin-nav-${item.key}`}
              title={item.label}
              style={{
                textDecoration: "none",
                color: active ? "#68280E" : "#2A2828",
                background: active ? "#FFFBC9" : "transparent",
                fontWeight: active ? 600 : 400,
                fontFamily: "Host Grotesk, sans-serif",
                fontSize: 14.5,
                padding: collapsed ? "10px 6px" : "10px 14px",
                borderRadius: 10,
                borderLeft: `3px solid ${active ? "#68280E" : "transparent"}`,
                display: "flex", alignItems: "center", gap: collapsed ? 0 : 12,
                justifyContent: collapsed ? "center" : "flex-start",
                position: "relative",
                transition: "background 0.15s ease",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 26, height: 26, borderRadius: 8,
                  background: active ? "#68280E" : "var(--folk-bg)",
                  color: active ? "#FFF" : "#2A2828",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, letterSpacing: 0,
                  flexShrink: 0,
                  border: "1.5px solid var(--folk-border)",
                  borderColor: active ? "#68280E" : "var(--folk-border)",
                }}
              >
                {item.icon}
              </span>
              {!collapsed && <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>}
              {badge != null && (
                <span
                  data-testid={`admin-nav-${item.key}-badge`}
                  style={{
                    background: "#68280E", color: "#FFF",
                    fontSize: 10.5, fontWeight: 700,
                    borderRadius: 999, padding: "1px 7px",
                    fontFamily: "Host Grotesk, sans-serif",
                    position: collapsed ? "absolute" : "static",
                    top: collapsed ? 4 : "auto",
                    right: collapsed ? 4 : "auto",
                  }}
                >
                  {badge}
                </span>
              )}
            </Link>
          );
        })}

        <div style={{ marginTop: "auto", paddingTop: 16, borderTop: "1.5px dashed var(--folk-border)", display: "flex", flexDirection: "column", gap: 10 }}>
          {user && !collapsed && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "0 8px", fontFamily: "Host Grotesk, sans-serif" }}>
              <div style={{ fontSize: 11, color: "var(--folk-hint)", textTransform: "uppercase", letterSpacing: "0.14em" }}>Signed in</div>
              <div style={{ fontSize: 12.5, color: "#2A2828", wordBreak: "break-all" }} data-testid="admin-shell-user">{user.email}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexDirection: collapsed ? "column" : "row", justifyContent: collapsed ? "center" : "space-between", padding: collapsed ? 0 : "0 8px" }}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              data-testid="admin-shell-collapse"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                background: "transparent", border: "1.5px solid var(--folk-border)",
                borderRadius: 999, cursor: "pointer",
                width: collapsed ? "100%" : 32, height: 32,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#2A2828", fontFamily: "Host Grotesk, sans-serif", fontSize: 12,
              }}
            >
              {collapsed ? "»" : "«"}
            </button>
            {user && (
              <button
                type="button"
                onClick={logout}
                data-testid="admin-shell-logout"
                title="Sign out"
                style={{
                  background: "transparent", border: "1.5px solid var(--folk-border)",
                  borderRadius: 999, cursor: "pointer",
                  padding: collapsed ? 0 : "6px 12px",
                  width: collapsed ? "100%" : "auto", height: 32,
                  fontSize: 12, color: "#2A2828", fontFamily: "Host Grotesk, sans-serif",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {collapsed ? "⎋" : "Sign out"}
              </button>
            )}
          </div>
        </div>
      </aside>

      <main style={{ padding: "40px 44px", minWidth: 0 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, marginBottom: 28, flexWrap: "wrap" }}>
            <div>
              {eyebrow && (
                <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 600 }}>{eyebrow}</div>
              )}
              <h1 className="folk-h1" style={{ marginTop: 6 }} data-testid="admin-shell-title">
                {title}
              </h1>
            </div>
            {right && <div>{right}</div>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
